import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { useToast } from "../context/ToastContext";

export interface ProductionEventLogEntry {
  eventId: string;
  workOrderId: string;
  workOrderNumber?: string;
  batchNumber?: string;
  subprocessId?: string;
  subprocessName?: string;
  machineId?: string;
  machineName?: string;
  statusCode: string;
  statusName: string;
  occurredAt?: string;
  recordedAt?: string;
  contemporaneityGapMin?: number;
  lateEntryReason?: string;
  performedByName?: string;
  verifiedByName?: string;
  signatureMeaning?: string;
  entryMethod?: string;
  isCorrection?: boolean;
  correctsEventId?: string;
  supersededByEventId?: string;
  documentRef?: { typeCode?: string; number?: string; lineId?: string };
  changeReasonText?: string;
}

/**
 * Fetches the production event log for a single work order.
 * Pages through ALL events (up to exhaustion). Never fires limit:9999.
 * workOrderId may be null/undefined — the hook does nothing in that case.
 */
export const useProductionEventLog = (workOrderId: string | null | undefined) => {
  const [events, setEvents] = useState<ProductionEventLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const apiUrl = process.env.REACT_APP_API_URL ?? "";
  const { showToast } = useToast();

  const fetchAll = useCallback(async (id: string) => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token missing");
      const headers = { "x-auth-token": token };

      let allEvents: ProductionEventLogEntry[] = [];
      let page = 1;
      let totalPages = 1;
      const PAGE_SIZE = 50;

      while (page <= totalPages) {
        if (reqId !== requestIdRef.current) return;
        const response = await axios.get<{
          success: boolean;
          data: ProductionEventLogEntry[];
          pagination: { page: number; limit: number; total: number; pages: number };
        }>(`${apiUrl}/api/production-event-log`, {
          headers,
          params: { workOrderId: id, page, limit: PAGE_SIZE, sort: "asc" },
        });
        allEvents = [...allEvents, ...(response.data.data ?? [])];
        totalPages = response.data.pagination?.pages ?? 1;
        page++;
      }

      if (reqId !== requestIdRef.current) return;
      setEvents(allEvents);
    } catch (err: any) {
      if (reqId !== requestIdRef.current) return;
      const message =
        err.response?.data?.message || err.message || "Failed to fetch event log";
      setError(message);
      setEvents([]);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    if (!workOrderId) {
      requestIdRef.current++;
      setEvents([]);
      setLoading(false);
      setError(null);
      return;
    }
    fetchAll(workOrderId);
  }, [workOrderId, fetchAll]);

  /** Download the event log as an Excel file. Returns false on error. */
  const downloadExcel = useCallback(
    async (id: string, filename?: string): Promise<boolean> => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("Authentication token missing");
        const response = await axios.get(
          `${apiUrl}/api/production-event-log/export`,
          {
            headers: { "x-auth-token": token },
            params: { workOrderId: id },
            responseType: "blob",
          }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename ?? `event-log-${id}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return true;
      } catch (err: any) {
        const message =
          err.response?.data?.message || err.message || "Export failed";
        showToast(message, "error");
        return false;
      }
    },
    [apiUrl, showToast]
  );

  return { events, loading, error, downloadExcel };
};
